import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Coins, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Wallet, Users,
  Banknote, Wrench, Gift, Sparkles, ArrowRight, Settings, Lock, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmtCurrency = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const enumerateDays = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const days = [];
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    days.push({ iso: `${y}-${m}-${d}`, day: cur.getDate(),
      weekday: cur.toLocaleDateString('en-AU', { weekday: 'short' }),
      isWeekend: cur.getDay() === 0 || cur.getDay() === 6 });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

// --- Creative Cash Flow Visualization ---
const CashRiverFlow = ({ data }) => {
  const cashIn = Math.max(0, Number(data.cash_accumulated) || 0);
  const wagesCash = Math.max(0, Number(data.wages_cash_paid) || 0);
  const incentives = Math.max(0, Number(data.total_incentives_paid) || 0);
  const parts = Math.max(0, Number(data.total_parts_consumed) || 0);
  const gstSaved = Math.max(0, Number(data.gst_saved) || 0);
  const remaining = Number(data.cash_remaining_after_wages) || 0;

  // For the flow bar, we show allocations that actually leave the till
  // plus the parts offset (notional) and remaining.
  const totalRef = Math.max(cashIn, wagesCash + incentives + Math.max(0, remaining));
  const pct = (v) => (totalRef > 0 ? Math.max(0, (v / totalRef) * 100) : 0);

  const segments = [
    { label: 'Cash Wages Paid', value: wagesCash, color: 'bg-orange-500', text: 'text-orange-50', icon: Banknote },
    { label: 'Incentives Paid', value: incentives, color: 'bg-purple-500', text: 'text-purple-50', icon: Sparkles },
    { label: 'Cash Remaining', value: Math.max(0, remaining), color: 'bg-emerald-500', text: 'text-emerald-50', icon: Wallet },
  ];
  const negative = remaining < 0;

  return (
    <Card className="p-6 bg-gradient-to-br from-slate-50 via-white to-slate-50 border-slate-200" data-testid="cash-river-flow">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            Cash River — where the money flows
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Starts with cash in hand, splits into wages, incentives, and what remains. Parts deducted from wages unlock GST savings.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Source</p>
          <p className="text-lg font-bold text-slate-900">{fmtCurrency(cashIn)}</p>
          <p className="text-[10px] text-slate-500">Cash in hand</p>
        </div>
      </div>

      {/* Flow bar */}
      <div className="relative w-full h-14 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 shadow-inner flex">
        {segments.map((s) => {
          const width = pct(s.value);
          if (width <= 0) return null;
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className={`${s.color} ${s.text} relative flex items-center justify-center transition-all duration-700 ease-out`}
              style={{ width: `${width}%` }}
              title={`${s.label}: ${fmtCurrency(s.value)}`}
            >
              {width > 12 && (
                <div className="flex items-center gap-1.5 px-2">
                  <Icon className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-semibold whitespace-nowrap">
                    {fmtCurrency(s.value)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend under bar */}
      <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center gap-2">
            <span className={`inline-block w-3 h-3 rounded-sm ${s.color}`} />
            <span className="text-slate-700">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Parts deduction + GST saved callout */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/60 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Wrench className="h-4 w-4 text-amber-700" />
            <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-800">Parts deducted from wages</p>
          </div>
          <p className="text-2xl font-bold text-amber-900" data-testid="parts-deducted-value">{fmtCurrency(parts)}</p>
          <p className="text-[11px] text-amber-700 mt-1">Employees purchased from the shop; offset against wages — no cash leaves the till.</p>
          <ArrowRight className="hidden md:block absolute top-1/2 -right-5 h-5 w-5 text-emerald-600 bg-white rounded-full p-0.5 border border-emerald-300 shadow-sm -translate-y-1/2" />
        </div>

        <div className="rounded-lg border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 shadow-sm" data-testid="gst-saved-card">
          <div className="flex items-center gap-2 mb-1">
            <Gift className="h-4 w-4 text-emerald-700" />
            <p className="text-[11px] uppercase tracking-wide font-semibold text-emerald-800">GST saved (AU)</p>
          </div>
          <p className="text-2xl font-bold text-emerald-900">{fmtCurrency(gstSaved)}</p>
          <p className="text-[11px] text-emerald-700 mt-1">
            <span className="font-mono bg-white/70 rounded px-1">parts ÷ 11</span> — internal offset vs retail sale.
          </p>
        </div>

        <div className={`rounded-lg border-2 p-4 shadow-sm ${negative ? 'border-red-300 bg-red-50' : 'border-blue-300 bg-blue-50'}`} data-testid="cash-holding-card">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className={`h-4 w-4 ${negative ? 'text-red-700' : 'text-blue-700'}`} />
            <p className={`text-[11px] uppercase tracking-wide font-semibold ${negative ? 'text-red-800' : 'text-blue-800'}`}>
              Cash holding remaining
            </p>
          </div>
          <p className={`text-2xl font-bold ${negative ? 'text-red-900' : 'text-blue-900'}`}>
            {negative ? '−' : ''}{fmtCurrency(Math.abs(remaining))}
          </p>
          <p className={`text-[11px] mt-1 ${negative ? 'text-red-700' : 'text-blue-700'}`}>
            {negative
              ? 'Allocations exceed cash on hand — review wage entries.'
              : 'After wages, parts offset & incentives allocations.'}
          </p>
        </div>
      </div>

      {/* Formula strip */}
      <div className="mt-4 px-4 py-3 rounded-md bg-slate-900 text-slate-100 text-[11px] font-mono flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-slate-400">formula:</span>
        <span>{fmtCurrency(cashIn)}</span>
        <span className="text-slate-500">−</span>
        <span className="text-orange-300">wages {fmtCurrency(data.total_wages)}</span>
        <span className="text-slate-500">+</span>
        <span className="text-amber-300">parts offset {fmtCurrency(parts)}</span>
        <span className="text-slate-500">−</span>
        <span className="text-purple-300">incentives {fmtCurrency(incentives)}</span>
        <span className="text-slate-500">=</span>
        <span className={negative ? 'text-red-300' : 'text-emerald-300'}>
          remaining {fmtCurrency(Math.abs(remaining))}
        </span>
      </div>
    </Card>
  );
};

// --- Payroll Setup Modal ---
function PayrollSetupModal({ BACKEND_URL, open, onOpenChange, onSaved }) {
  const [payroll, setPayroll] = useState([]);
  const [edits, setEdits] = useState({}); // {employee_id: newWeeklyWage}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Effective date for wage changes. Defaults to today so the change
  // applies from now onwards without retroactively touching past
  // daily-entry / cash-flow figures. SM can adjust if the wage change
  // actually took effect earlier (e.g. employee left two weeks ago).
  const [effectiveFrom, setEffectiveFrom] = useState(
    () => new Date().toISOString().slice(0, 10),
  );

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/cash-incentives/payroll`,
        { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setPayroll(json.payroll || []);
        setEdits({});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [BACKEND_URL]);

  useEffect(() => { if (open) fetchConfig(); }, [open, fetchConfig]);

  const handleSaveAll = async () => {
    // Diff-based: only POST rows where any of the editable fields changed
    const byId = Object.fromEntries(payroll.map(p => [p.employee_id, p]));
    const changes = [];
    Object.entries(edits).forEach(([empId, vals]) => {
      const cur = byId[empId];
      if (!cur) return;
      // vals may be a primitive (legacy weekly_wage) or an object
      const isObj = typeof vals === 'object' && vals !== null;
      const w = parseFloat(isObj ? (vals.weekly_wage ?? cur.weekly_wage) : vals);
      const bp = parseFloat(isObj ? (vals.bonus_pct ?? cur.bonus_pct) : cur.bonus_pct);
      const al = parseFloat(isObj ? (vals.annual_leave_hours_per_week ?? cur.annual_leave_hours_per_week) : cur.annual_leave_hours_per_week);
      const sw = parseFloat(isObj ? (vals.sick_leave_hours_per_week ?? cur.sick_leave_hours_per_week) : cur.sick_leave_hours_per_week);
      const lsw = parseFloat(isObj ? (vals.long_service_leave_hours_per_week ?? cur.long_service_leave_hours_per_week) : cur.long_service_leave_hours_per_week);
      if (isNaN(w) || w < 0) return;
      const changed = (
        Math.abs((w || 0) - (cur.weekly_wage || 0)) > 0.0001 ||
        Math.abs((bp || 0) - (cur.bonus_pct || 0)) > 0.0001 ||
        Math.abs((al || 0) - (cur.annual_leave_hours_per_week || 0)) > 0.0001 ||
        Math.abs((sw || 0) - (cur.sick_leave_hours_per_week || 0)) > 0.0001 ||
        Math.abs((lsw || 0) - (cur.long_service_leave_hours_per_week || 0)) > 0.0001
      );
      if (changed) changes.push({ empId, w, bp, al, sw, lsw });
    });
    if (changes.length === 0) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      for (const { empId, w, bp, al, sw, lsw } of changes) {
        await fetch(`${BACKEND_URL}/api/super-admin/cash-incentives/payroll`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employee_id: empId, weekly_wage: w,
            // Wage edits are time-effective — past months keep showing
            // the OLD wage; new wage takes over from `effective_from`
            // onwards (defaults to today).
            effective_from: effectiveFrom,
            bonus_pct: isNaN(bp) ? 0 : bp,
            annual_leave_hours_per_week: isNaN(al) ? 0 : al,
            sick_leave_hours_per_week: isNaN(sw) ? 0 : sw,
            long_service_leave_hours_per_week: isNaN(lsw) ? 0 : lsw,
          }),
        });
      }
      toast.success(
        `Updated ${changes.length} employee config${changes.length === 1 ? '' : 's'} — effective from ${effectiveFrom}`,
      );
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error('Failed to save some configs');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="payroll-setup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-indigo-600" /> Configure Wages, Bonus & Leave
          </DialogTitle>
          <DialogDescription>
          One-row-per-employee configuration:
          <strong>Weekly wage</strong> auto-fills the daily grid ·{' '}
          <strong>Bonus %</strong> on extra hours beyond 7.6h/day ·{' '}
          <strong>Per-week leave hours</strong> accrue daily into Annual / Sick / Long Service balances.
          </DialogDescription>
        </DialogHeader>

        {/* Effective-date picker for wage edits — defaults to today so
            changes apply from now onwards without retroactively touching
            past daily entries. SM can backdate if needed. */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-md text-xs">
          <div className="flex items-center gap-1.5 text-amber-800 font-medium">
            <Lock className="h-3.5 w-3.5" /> Wage changes effective from:
          </div>
          <input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value || new Date().toISOString().slice(0, 10))}
            className="border border-amber-300 rounded px-2 py-1 text-xs bg-white"
            data-testid="payroll-effective-from"
          />
          <span className="text-slate-600 italic">
            Past daily entries before this date keep the OLD wage.
            New wage applies from {effectiveFrom} forward.
          </span>
        </div>

        {loading ? (
          <div className="h-32 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-4 border-indigo-600 border-t-transparent" />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border rounded-md">
            <table className="min-w-full text-sm" data-testid="payroll-setup-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Employee</th>
                  <th className="p-2 text-left w-20">Role</th>
                  <th className="p-2 text-right w-24">Weekly $</th>
                  <th className="p-2 text-right w-16">Bonus %</th>
                  <th className="p-2 text-right w-20">Annual h/wk</th>
                  <th className="p-2 text-right w-20">Sick h/wk</th>
                  <th className="p-2 text-right w-20">LSL h/wk</th>
                </tr>
              </thead>
              <tbody>
                {payroll.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-slate-500">
                    No eligible employees found for this site.
                  </td></tr>
                ) : payroll.map((p) => {
                  const cur = (typeof edits[p.employee_id] === 'object' ? edits[p.employee_id] : null) || {};
                  const setField = (k, v) => setEdits(prev => ({
                    ...prev,
                    [p.employee_id]: { ...(typeof prev[p.employee_id] === 'object' ? prev[p.employee_id] : {}), [k]: v }
                  }));
                  return (
                    <tr key={p.employee_id} className={`hover:bg-slate-50 ${p.excluded ? 'opacity-60' : ''}`}>
                      <td className="p-2">
                        <div className="font-medium text-slate-900">{p.employee_name}</div>
                        {p.excluded && <div className="text-[10px] text-slate-500">excluded from productivity</div>}
                      </td>
                      <td className="p-2 text-xs">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] capitalize ${
                          ['service_manager', 'floor_manager'].includes(p.role) ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {p.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.01" min="0"
                          defaultValue={p.weekly_wage || ''} placeholder="0"
                          onChange={(e) => setField('weekly_wage', e.target.value)}
                          className="text-right h-8 text-sm"
                          data-testid={`payroll-wage-${p.employee_id}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="1" min="0" max="100"
                          defaultValue={p.bonus_pct || ''} placeholder="0"
                          onChange={(e) => setField('bonus_pct', e.target.value)}
                          className="text-right h-8 text-sm"
                          data-testid={`payroll-bonus-${p.employee_id}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.5" min="0" max="80"
                          defaultValue={p.annual_leave_hours_per_week || ''} placeholder="0"
                          onChange={(e) => setField('annual_leave_hours_per_week', e.target.value)}
                          className="text-right h-8 text-sm"
                          data-testid={`payroll-al-${p.employee_id}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.5" min="0" max="80"
                          defaultValue={p.sick_leave_hours_per_week || ''} placeholder="0"
                          onChange={(e) => setField('sick_leave_hours_per_week', e.target.value)}
                          className="text-right h-8 text-sm"
                          data-testid={`payroll-sw-${p.employee_id}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.5" min="0" max="80"
                          defaultValue={p.long_service_leave_hours_per_week || ''} placeholder="0"
                          onChange={(e) => setField('long_service_leave_hours_per_week', e.target.value)}
                          className="text-right h-8 text-sm"
                          data-testid={`payroll-lsl-${p.employee_id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSaveAll} disabled={saving || loading} data-testid="payroll-setup-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CashIncentivesTab({ BACKEND_URL }) {
  const today = new Date();
  const [view, setView] = useState('monthly');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [week, setWeek] = useState(1);
  const [rangeStart, setRangeStart] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
  const [rangeEnd, setRangeEnd] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedMechanics, setExpandedMechanics] = useState({});
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  // Opening balance anchor modal — { open, mech }
  const [openingBalanceModal, setOpeningBalanceModal] = useState({ open: false, mech: null });
  const debouncers = useRef({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (view === 'range') {
        params.append('start_date', rangeStart);
        params.append('end_date', rangeEnd);
      } else {
        params.append('year', year);
        if (view !== 'yearly') params.append('month', month);
        if (view === 'weekly') params.append('week', week);
      }
      const res = await fetch(`${BACKEND_URL}/api/super-admin/cash-incentives?${params}`,
        { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      console.error('Failed to load cash incentives:', e);
    } finally { setLoading(false); }
  }, [BACKEND_URL, view, year, month, week, rangeStart, rangeEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /**
   * Print-friendly per-employee report for the currently selected period.
   * Opens a new window with the response rendered as a clean printable
   * pay-sheet. SM can then either Print or save as PDF from the browser
   * dialog — no server-side PDF dependency.
   */
  const generateEmployeeReport = useCallback(async (mechanicId, mechanicName) => {
    if (!data) return;
    const params = new URLSearchParams({
      mechanic_id: mechanicId,
      start_date: data.start_date,
      end_date: data.end_date,
    });
    const toastId = toast.loading(`Building report for ${mechanicName}…`);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/super-admin/cash-incentives/employee-report?${params}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed (HTTP ${res.status})`, { id: toastId });
        return;
      }
      const report = await res.json();
      toast.success(`Report ready — opening print window`, { id: toastId });
      const win = window.open('', '_blank', 'width=900,height=1100');
      if (!win) {
        toast.error('Pop-up blocked. Allow pop-ups and try again.');
        return;
      }
      const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
      const balCls = (n) => (Number(n) < 0 ? 'color:#b91c1c' : 'color:#047857');
      const rowsHtml = report.rows.map((r) => `
        <tr style="${r.is_working_day ? '' : 'background:#f8fafc;color:#64748b;'}">
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">${r.date} <span style="color:#94a3b8;">(${r.day_of_week})</span></td>
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(r.wage)}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;text-align:right;color:#b45309;">${fmt(r.parts_consumed)}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;text-align:right;color:#0284c7;">${fmt(r.wage_paid)}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;text-align:right;color:#7c3aed;">${fmt(r.incentive)}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;${balCls(r.balance_change)}">${fmt(r.balance_change)}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#64748b;">${(r.notes || '').replace(/</g, '&lt;')}</td>
        </tr>
      `).join('');
      win.document.write(`
        <!doctype html><html><head><title>Cash Report — ${report.employee.name}</title>
        <style>
          body { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; padding: 24px; color: #0f172a; }
          h1 { font-size: 18px; margin: 0 0 4px; }
          h2 { font-size: 14px; margin: 18px 0 8px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
          th { padding: 6px 8px; text-align: left; background: #f1f5f9; border-bottom: 2px solid #cbd5e1; font-weight: 600; }
          .totals { margin-top: 16px; background: #f8fafc; padding: 12px; border-radius: 6px; }
          .balance { padding: 6px 12px; border-radius: 6px; display: inline-block; font-weight: 700; font-size: 14px; }
          .balance.pos { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
          .balance.neg { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
          .meta { font-size: 11px; color: #64748b; }
          @media print { @page { margin: 14mm; } button { display: none; } }
        </style></head>
        <body>
          <button onclick="window.print()" style="float:right;padding:6px 12px;background:#0f172a;color:#fff;border:0;border-radius:6px;cursor:pointer;">Print / Save PDF</button>
          <h1>Cash Incentives Report</h1>
          <div class="meta">${report.employee.name} (${report.employee.role}) · ${report.start_date} → ${report.end_date} · generated ${new Date(report.generated_at).toLocaleString()} by ${report.generated_by}</div>
          ${(report.employee.weekly_wage_configured > 0) ? `<div class="meta">Weekly wage configured (current): ${fmt(report.employee.weekly_wage_configured)}</div>` : ''}

          <h2>Daily Breakdown</h2>
          <table>
            <thead><tr>
              <th>Date</th>
              <th style="text-align:right;">Wage</th>
              <th style="text-align:right;">Parts</th>
              <th style="text-align:right;">Wage Paid</th>
              <th style="text-align:right;">Incentive</th>
              <th style="text-align:right;">Net (Bal Δ)</th>
              <th>Notes</th>
            </tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>

          <div class="totals">
            <div>Opening balance (as of ${report.start_date}): <strong style="${balCls(report.opening_balance)}">${fmt(report.opening_balance)}</strong></div>
            <div style="margin-top:6px;">
              Period totals:
              Wage <strong>${fmt(report.totals.wage)}</strong>
              · Parts <strong>${fmt(report.totals.parts_consumed)}</strong>
              · Wage Paid <strong>${fmt(report.totals.wage_paid)}</strong>
              · Incentive <strong>${fmt(report.totals.incentive)}</strong>
            </div>
            <div style="margin-top:6px;">Period balance change: <strong style="${balCls(report.period_balance)}">${fmt(report.period_balance)}</strong></div>
            <div style="margin-top:10px;">Closing balance:
              <span class="balance ${report.closing_balance < 0 ? 'neg' : 'pos'}">
                ${fmt(Math.abs(report.closing_balance))}
                ${report.closing_balance < 0 ? '— mechanic owes employer' : (report.closing_balance > 0 ? '— employer owes mechanic' : '— settled')}
              </span>
            </div>
          </div>
        </body></html>
      `);
      win.document.close();
    } catch (_e) {
      toast.error('Network error — please retry', { id: toastId });
    }
  }, [BACKEND_URL, data]);

  const days = useMemo(
    () => data ? enumerateDays(data.start_date, data.end_date) : [],
    [data]
  );

  const saveCell = useCallback((mechanicId, date, field, value, currentEntry) => {
    const key = `${mechanicId}_${date}_${field}`;
    if (debouncers.current[key]) clearTimeout(debouncers.current[key]);
    debouncers.current[key] = setTimeout(async () => {
      // iter136 — balance_to_pay_override is allowed to be null to revert to auto
      const overrideVal = field === 'balance_to_pay_override'
        ? (value === '' || value == null ? null : value)
        : (currentEntry?.balance_to_pay_override ?? null);
      const body = {
        mechanic_id: mechanicId,
        date,
        incentive: field === 'incentive' ? value : (currentEntry?.incentive || 0),
        parts_consumed: field === 'parts_consumed' ? value : (currentEntry?.parts_consumed || 0),
        wage: field === 'wage' ? value : (currentEntry?.wage || 0),
        wage_paid: field === 'wage_paid' ? value : (currentEntry?.wage_paid || 0),
        balance_to_pay_override: overrideVal,
      };
      try {
        const res = await fetch(`${BACKEND_URL}/api/super-admin/cash-incentives`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) toast.error('Failed to save'); else fetchData();
      } catch (e) {
        toast.error('Failed to save');
      }
    }, 600);
  }, [BACKEND_URL, fetchData]);

  const handleCellChange = (mechanicId, date, field, raw, currentEntry) => {
    // iter136 — special handling for balance_to_pay_override (can be negative + null-clearing)
    if (field === 'balance_to_pay_override') {
      // Empty string = clear override (revert to auto)
      let nextVal;
      if (raw === '' || raw == null) {
        nextVal = null;
      } else {
        const num = parseFloat(raw);
        if (isNaN(num)) return;
        nextVal = num;
      }
      setData((prev) => {
        if (!prev) return prev;
        const mechs = prev.per_mechanic.map((m) => {
          if (m.mechanic_id !== mechanicId) return m;
          const entries = { ...(m.entries || {}) };
          const cell = { ...(entries[date] || { incentive: 0, parts_consumed: 0, wage: 0, wage_paid: 0, balance_to_pay_override: null }), balance_to_pay_override: nextVal };
          entries[date] = cell;
          return { ...m, entries };
        });
        return { ...prev, per_mechanic: mechs };
      });
      saveCell(mechanicId, date, field, nextVal, currentEntry);
      return;
    }
    const num = raw === '' ? 0 : parseFloat(raw);
    if (isNaN(num) || num < 0) return;
    // Optimistic: mutate local copy so the input reflects user typing
    setData((prev) => {
      if (!prev) return prev;
      const mechs = prev.per_mechanic.map((m) => {
        if (m.mechanic_id !== mechanicId) return m;
        const entries = { ...(m.entries || {}) };
        const cell = { ...(entries[date] || { incentive: 0, parts_consumed: 0, wage: 0, wage_paid: 0 }), [field]: num };
        entries[date] = cell;
        return { ...m, entries };
      });
      return { ...prev, per_mechanic: mechs };
    });
    saveCell(mechanicId, date, field, num, currentEntry);
  };

  const toggleMech = (id) => setExpandedMechanics((prev) => ({ ...prev, [id]: !prev[id] }));

  if (loading && !data) {
    return (
      <Card className="p-6"><div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
      </div></Card>
    );
  }
  if (!data) return null;

  const cashFlowOk = data.is_positive;
  const cashRemaining = data.cash_remaining_after_wages ?? data.cash_remaining;

  return (
    <div className="space-y-6" data-testid="cash-incentives-tab">
      {/* Header / Filters */}
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Cash Incentives & Cash Flow
            </h3>
            <p className="text-sm text-slate-500">
              Cash auto-fetched from daily entries. Allocate wages, incentives & parts per mechanic.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[130px]" data-testid="ci-view-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
              </SelectContent>
            </Select>
            {view !== 'range' && (
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {view !== 'range' && (
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {view === 'weekly' && (
              <Select value={week.toString()} onValueChange={(v) => setWeek(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((w) => <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {view === 'range' && (
              <>
                <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-[160px]" data-testid="ci-range-start" />
                <span className="text-slate-400">to</span>
                <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="w-[160px]" data-testid="ci-range-end" />
              </>
            )}
            <Button variant="outline" onClick={() => setShowPayrollModal(true)} className="gap-2" data-testid="configure-wages-btn">
              <Settings className="h-4 w-4" /> Configure Wages
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="kpi-cash-acc">
            <p className="text-xs text-blue-600 uppercase tracking-wide flex items-center gap-1"><Wallet className="h-3 w-3" />Cash in Hand</p>
            <p className="text-xl font-bold text-blue-900 mt-1">{fmtCurrency(data.cash_accumulated)}</p>
            <p className="text-[11px] text-blue-500 mt-1">From daily entries</p>
          </div>
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="kpi-wages">
            <p className="text-xs text-orange-600 uppercase tracking-wide flex items-center gap-1"><Banknote className="h-3 w-3" />Wages Allocated</p>
            <p className="text-xl font-bold text-orange-900 mt-1">{fmtCurrency(data.total_wages || 0)}</p>
            <p className="text-[11px] text-orange-500 mt-1">gross per mechanic</p>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="kpi-parts">
            <p className="text-xs text-amber-600 uppercase tracking-wide flex items-center gap-1"><Wrench className="h-3 w-3" />Parts Deducted</p>
            <p className="text-xl font-bold text-amber-900 mt-1">{fmtCurrency(data.total_parts_consumed || 0)}</p>
            <p className="text-[11px] text-amber-500 mt-1">offsets wages</p>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg" data-testid="kpi-incentives">
            <p className="text-xs text-purple-600 uppercase tracking-wide flex items-center gap-1"><Sparkles className="h-3 w-3" />Incentives</p>
            <p className="text-xl font-bold text-purple-900 mt-1">{fmtCurrency(data.total_incentives_paid)}</p>
            <p className="text-[11px] text-purple-500 mt-1">{data.per_mechanic.length} mechanics</p>
          </div>
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="kpi-gst-saved">
            <p className="text-xs text-emerald-600 uppercase tracking-wide flex items-center gap-1"><Gift className="h-3 w-3" />GST Saved</p>
            <p className="text-xl font-bold text-emerald-900 mt-1">{fmtCurrency(data.gst_saved || 0)}</p>
            <p className="text-[11px] text-emerald-500 mt-1">parts ÷ 11</p>
          </div>
          <div
            className={`p-4 border rounded-lg ${cashFlowOk ? 'bg-teal-50 border-teal-300' : 'bg-red-50 border-red-300'}`}
            data-testid="kpi-cash-remaining"
          >
            <p className={`text-xs uppercase tracking-wide flex items-center gap-1 ${cashFlowOk ? 'text-teal-700' : 'text-red-700'}`}>
              {cashFlowOk ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              Cash Remaining
            </p>
            <p className={`text-xl font-bold mt-1 ${cashFlowOk ? 'text-teal-900' : 'text-red-900'}`}>
              {cashFlowOk ? '' : '−'}{fmtCurrency(Math.abs(cashRemaining))}
            </p>
            <p className={`text-[11px] mt-1 ${cashFlowOk ? 'text-teal-600' : 'text-red-600'}`}>
              {cashFlowOk ? 'after wages + incentives' : 'NEGATIVE — review entries'}
            </p>
          </div>
        </div>
      </Card>

      {/* Creative Cash Flow visualization */}
      <CashRiverFlow data={data} />

      {/* Per-Mechanic Collapsible Grids */}
      <div className="space-y-3">
        {data.per_mechanic.length === 0 ? (
          <Card className="p-8 text-center text-slate-500">
            <Users className="h-10 w-10 mx-auto mb-3 text-slate-400" />
            No mechanics found. Add mechanics or check excluded list under Productivity tab.
          </Card>
        ) : data.per_mechanic.map((m) => {
          const isOpen = !!expandedMechanics[m.mechanic_id];
          // Math the SM cares about (verbatim from their words):
          //   Total Cash Wage    = m.total_wages
          //   − Parts from Shop  = m.total_parts
          //   = Balance to Pay   = max(0, wage − parts)         (= m.cash_paid_out)
          //   + Incentive        = m.total_incentives           (cash bonus on top)
          //   = Cash Out         = balance + incentive
          const balanceToPay = Math.max(0, (m.total_wages || 0) - (m.total_parts || 0));
          const totalCashOut = balanceToPay + (m.total_incentives || 0);
          return (
            <Card key={m.mechanic_id} className="overflow-hidden" data-testid={`ci-mech-${m.mechanic_id}`}>
              <button
                onClick={() => toggleMech(m.mechanic_id)}
                className="w-full px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid={`ci-mech-toggle-${m.mechanic_id}`}
              >
                {/* Top row — name + role/wage badges + chevron */}
                <div className="flex items-center gap-3 mb-2">
                  {isOpen ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                  <span className="font-semibold text-slate-900 truncate">{m.mechanic_name}</span>
                  {(m.role === 'service_manager' || m.role === 'floor_manager') && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium flex-shrink-0">
                      {m.role === 'floor_manager' ? 'Floor Manager' : 'Service Manager'}
                    </span>
                  )}
                  {m.weekly_wage_configured > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium flex items-center gap-1 flex-shrink-0" title={`Auto wage: $${m.weekly_wage_configured}/week`}>
                      <Lock className="h-2.5 w-2.5" /> ${m.weekly_wage_configured}/wk
                    </span>
                  )}
                </div>
                {/* Math row — reads left-to-right like the SM's own sentence:
                    "Wage MINUS Parts EQUALS Balance to Pay [PLUS Incentive
                    EQUALS Cash Out]". Balance is the headline — that's the
                    number the SM hands to the mechanic. */}
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-sm" data-testid={`ci-mech-math-${m.mechanic_id}`}>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">Total Cash Wage</span>
                    <span className="font-bold text-orange-700">{fmtCurrency(m.total_wages || 0)}</span>
                  </div>
                  <span className="text-slate-400 font-bold text-lg mt-3">−</span>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">Parts from Shop</span>
                    <span className="font-bold text-amber-700">{fmtCurrency(m.total_parts || 0)}</span>
                  </div>
                  <span className="text-slate-400 font-bold text-lg mt-3">=</span>
                  <div className="flex flex-col items-start rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1" data-testid={`ci-mech-balance-${m.mechanic_id}`}>
                    <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Balance to Pay</span>
                    <span className="font-extrabold text-emerald-800 text-base">{fmtCurrency(balanceToPay)}</span>
                  </div>
                  {(m.total_incentives || 0) > 0 && (
                    <>
                      <span className="text-slate-400 font-bold text-lg mt-3">+</span>
                      <div className="flex flex-col items-start">
                        <span className="text-[10px] uppercase tracking-wide text-slate-500">Incentive</span>
                        <span className="font-bold text-purple-700">{fmtCurrency(m.total_incentives || 0)}</span>
                      </div>
                      <span className="text-slate-400 font-bold text-lg mt-3">=</span>
                      <div className="flex flex-col items-start rounded-md bg-slate-100 border border-slate-300 px-2 py-1">
                        <span className="text-[10px] uppercase tracking-wide text-slate-600 font-semibold">Cash Out</span>
                        <span className="font-bold text-slate-800">{fmtCurrency(totalCashOut)}</span>
                      </div>
                    </>
                  )}
                </div>
              </button>
              {isOpen && (
                <div className="p-4 overflow-x-auto">
                  <table className="min-w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border p-2 text-left sticky left-0 bg-slate-100 min-w-32">Field</th>
                        {days.map((d) => (
                          <th key={d.iso} className={`border p-1 text-center min-w-14 ${d.isWeekend ? 'bg-slate-200' : ''}`}>
                            <div className="text-[10px] text-slate-500">{d.weekday}</div>
                            <div className="font-bold">{d.day}</div>
                          </th>
                        ))}
                        <th className="border p-2 text-center bg-slate-200 font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border p-2 sticky left-0 bg-orange-50 font-medium">
                          💵 Wage ($)
                          {m.weekly_wage_configured > 0 && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] text-indigo-700 bg-indigo-100 px-1 rounded" title={`Auto: $${m.weekly_wage_configured}/week`}>
                              <Lock className="h-2.5 w-2.5" />auto
                            </span>
                          )}
                        </td>
                        {days.map((d) => {
                          const entry = m.entries?.[d.iso];
                          const v = entry?.wage || 0;
                          const isAuto = entry?.wage_source === 'auto';
                          return (
                            <td key={d.iso} className={`border p-0 relative ${d.isWeekend ? 'bg-slate-50' : ''}`}>
                              <input
                                type="number" step="0.01" min="0"
                                value={v || ''}
                                onChange={(e) => handleCellChange(m.mechanic_id, d.iso, 'wage', e.target.value, entry)}
                                className={`w-full h-8 text-center text-xs border-0 focus:outline-none focus:bg-orange-50 ${
                                  isAuto
                                    ? 'bg-indigo-50/70 text-indigo-700 italic'
                                    : v > 0 ? 'bg-orange-50' : ''
                                }`}
                                placeholder="—"
                                title={isAuto ? 'Auto-filled from weekly wage config. Type to override.' : undefined}
                                data-testid={`wage-${m.mechanic_id}-${d.iso}`}
                              />
                              {isAuto && v > 0 && (
                                <Lock className="h-2.5 w-2.5 text-indigo-400 absolute top-0.5 right-0.5 pointer-events-none" />
                              )}
                            </td>
                          );
                        })}
                        <td className="border p-2 text-center font-bold bg-orange-100">{fmtCurrency(m.total_wages || 0)}</td>
                      </tr>
                      <tr>
                        <td className="border p-2 sticky left-0 bg-amber-50 font-medium">🔧 Parts from Shop ($)</td>
                        {days.map((d) => {
                          const entry = m.entries?.[d.iso];
                          const v = entry?.parts_consumed || 0;
                          return (
                            <td key={d.iso} className={`border p-0 ${d.isWeekend ? 'bg-slate-50' : ''}`}>
                              <input
                                type="number" step="0.01" min="0"
                                value={v || ''}
                                onChange={(e) => handleCellChange(m.mechanic_id, d.iso, 'parts_consumed', e.target.value, entry)}
                                className={`w-full h-8 text-center text-xs border-0 focus:outline-none focus:bg-amber-50 ${v > 0 ? 'bg-amber-50' : ''}`}
                                placeholder="—"
                                data-testid={`parts-${m.mechanic_id}-${d.iso}`}
                              />
                            </td>
                          );
                        })}
                        <td className="border p-2 text-center font-bold bg-amber-100">{fmtCurrency(m.total_parts)}</td>
                      </tr>
                      {/* Wage Paid — editable. SM enters how much cash was
                          actually paid out to the mechanic on this day.
                          Drives the running BALANCE OWING calculation.
                          Default 0; SM enters as wage is handed over. */}
                      <tr>
                        <td className="border p-2 sticky left-0 bg-sky-50 font-medium" title="Cash actually handed to the mechanic for this day's wage. Default 0 — fill in as you pay.">
                          💸 Wage Paid ($)
                        </td>
                        {days.map((d) => {
                          const entry = m.entries?.[d.iso];
                          const v = entry?.wage_paid || 0;
                          return (
                            <td key={d.iso} className={`border p-0 ${d.isWeekend ? 'bg-slate-50' : ''}`}>
                              <input
                                type="number" step="0.01" min="0"
                                value={v || ''}
                                onChange={(e) => handleCellChange(m.mechanic_id, d.iso, 'wage_paid', e.target.value, entry)}
                                className={`w-full h-8 text-center text-xs border-0 focus:outline-none focus:bg-sky-50 ${v > 0 ? 'bg-sky-50' : ''}`}
                                placeholder="—"
                                data-testid={`wagepaid-${m.mechanic_id}-${d.iso}`}
                              />
                            </td>
                          );
                        })}
                        <td className="border p-2 text-center font-bold bg-sky-100">{fmtCurrency(m.total_wage_paid || 0)}</td>
                      </tr>
                      {/* iter136 — Balance to Pay is now EDITABLE per-cell.
                          Default value = wage − parts (no clamp at 0 so a
                          parts-heavy day can go negative). Typing in the
                          field stores `balance_to_pay_override` which is
                          then used by the Balance Owing running formula
                          below in place of (wage − parts). Clear the field
                          to revert to auto. Blue background = manual override. */}
                      <tr>
                        <td className="border p-2 sticky left-0 bg-emerald-50 font-semibold text-emerald-900">
                          🟢 Balance to Pay ($)
                        </td>
                        {days.map((d) => {
                          const entry = m.entries?.[d.iso];
                          const w = entry?.wage || 0;
                          const p = entry?.parts_consumed || 0;
                          const override = entry?.balance_to_pay_override;
                          const hasOverride = override != null && override !== '';
                          const auto = w - p;
                          const displayed = hasOverride ? Number(override) : auto;
                          return (
                            <td key={d.iso} className={`border p-0 text-center ${d.isWeekend ? 'bg-slate-50' : ''}`}>
                              <input
                                type="number" step="0.01"
                                value={hasOverride ? override : (auto !== 0 ? auto : '')}
                                onChange={(e) => handleCellChange(m.mechanic_id, d.iso, 'balance_to_pay_override', e.target.value, entry)}
                                onBlur={(e) => {
                                  if (e.target.value.trim() === '' && hasOverride) {
                                    // Clear override → revert to auto
                                    handleCellChange(m.mechanic_id, d.iso, 'balance_to_pay_override', '', entry);
                                  }
                                }}
                                title={hasOverride
                                  ? `Manual override · auto would be ${auto.toFixed(2)} (wage ${w} − parts ${p})`
                                  : `Auto = wage ${w} − parts ${p}. Type to override.`}
                                placeholder={auto !== 0 ? auto.toFixed(2) : '—'}
                                className={`w-full h-8 text-center text-xs border-0 focus:outline-none focus:bg-emerald-100 ${
                                  hasOverride
                                    ? 'bg-blue-50 font-bold text-blue-800'
                                    : displayed > 0
                                      ? 'text-emerald-800 bg-emerald-50 font-semibold'
                                      : displayed < 0
                                        ? 'text-rose-700 bg-rose-50 font-semibold'
                                        : 'text-slate-400'
                                }`}
                                data-testid={`balance-${m.mechanic_id}-${d.iso}`}
                              />
                            </td>
                          );
                        })}
                        <td className="border p-2 text-center font-extrabold bg-emerald-100 text-emerald-900">
                          {fmtCurrency((m.total_wages || 0) - (m.total_parts || 0))}
                        </td>
                      </tr>
                      <tr>
                        <td className="border p-2 sticky left-0 bg-purple-50 font-medium">💰 Incentive ($)</td>
                        {days.map((d) => {
                          const entry = m.entries?.[d.iso];
                          const v = entry?.incentive || 0;
                          return (
                            <td key={d.iso} className={`border p-0 ${d.isWeekend ? 'bg-slate-50' : ''}`}>
                              <input
                                type="number" step="0.01" min="0"
                                value={v || ''}
                                onChange={(e) => handleCellChange(m.mechanic_id, d.iso, 'incentive', e.target.value, entry)}
                                className={`w-full h-8 text-center text-xs border-0 focus:outline-none focus:bg-purple-50 ${v > 0 ? 'bg-purple-50' : ''}`}
                                placeholder="—"
                                data-testid={`incentive-${m.mechanic_id}-${d.iso}`}
                              />
                            </td>
                          );
                        })}
                        <td className="border p-2 text-center font-bold bg-purple-100">{fmtCurrency(m.total_incentives)}</td>
                      </tr>
                      {/* Balance Owing — running tally. The headline KPI:
                          positive ⇒ employer owes mechanic; negative ⇒
                          mechanic owes employer (he took more parts than
                          his wage covered). Includes opening balance from
                          before this period so it's the true running
                          total. */}
                      <tr>
                        <td className="border p-2 sticky left-0 bg-slate-100 font-bold text-slate-900" title="Running balance per day. Cumulative from opening balance + (wage − parts − wage_paid).">
                          📒 Balance Owing ($)
                        </td>
                        {(() => {
                          let running = m.opening_balance || 0;
                          return days.map((d) => {
                            const entry = m.entries?.[d.iso];
                            const w = entry?.wage || 0;
                            const p = entry?.parts_consumed || 0;
                            const wp = entry?.wage_paid || 0;
                            // iter136 (user choice 3.1 = A) — Balance to Pay
                            // override REPLACES the auto (wage − parts) in
                            // the running formula. If no override set, use
                            // the regular (wage − parts).
                            const override = entry?.balance_to_pay_override;
                            const btp = (override != null && override !== '') ? Number(override) : (w - p);
                            running += (btp - wp);
                            const isNeg = running < 0;
                            return (
                              <td key={d.iso} className={`border p-0 text-center ${d.isWeekend ? 'bg-slate-50' : ''}`}>
                                <span
                                  className={`block h-8 leading-8 text-xs font-bold ${
                                    Math.abs(running) < 0.005
                                      ? 'text-slate-400'
                                      : isNeg
                                        ? 'text-rose-700 bg-rose-50'
                                        : 'text-emerald-800 bg-emerald-50'
                                  }`}
                                  data-testid={`balance-owing-${m.mechanic_id}-${d.iso}`}
                                  title={isNeg ? 'Mechanic owes employer' : (running > 0 ? 'Employer owes mechanic' : 'Settled')}
                                >
                                  {Math.abs(running) < 0.005 ? '—' : fmtCurrency(running)}
                                </span>
                              </td>
                            );
                          });
                        })()}
                        <td className={`border p-2 text-center font-extrabold ${(m.balance_owing || 0) < 0 ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-900'}`}>
                          {fmtCurrency(m.balance_owing || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Per-employee period summary — the user's account-card
                      view: "Wage − Parts − Wage Paid = Balance Owing".
                      Plus a Print Report button for individual employee
                      records for any custom period. */}
                  <div className="mt-4 p-3 bg-slate-50 border rounded-md flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid={`ci-mech-summary-${m.mechanic_id}`}>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">Opening</span>
                      <span className={`font-semibold ${(m.opening_balance || 0) < 0 ? 'text-rose-700' : 'text-slate-700'}`}>{fmtCurrency(m.opening_balance || 0)}</span>
                      {m.opening_balance_override != null && (
                        <span
                          className="ml-1 text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded"
                          title={`Anchored at ${fmtCurrency(m.opening_balance_override)} as of ${m.opening_balance_as_of}`}
                        >anchored</span>
                      )}
                    </div>
                    <span className="text-slate-300">+</span>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">Wage</span>
                      <span className="font-semibold text-orange-700">{fmtCurrency(m.total_wages || 0)}</span>
                    </div>
                    <span className="text-slate-300">−</span>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">Parts</span>
                      <span className="font-semibold text-amber-700">{fmtCurrency(m.total_parts || 0)}</span>
                    </div>
                    <span className="text-slate-300">−</span>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">Wage Paid</span>
                      <span className="font-semibold text-sky-700">{fmtCurrency(m.total_wage_paid || 0)}</span>
                    </div>
                    <span className="text-slate-300">=</span>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded font-bold ${(m.balance_owing || 0) < 0 ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-900'}`}>
                      <span className="text-[10px] uppercase">{(m.balance_owing || 0) < 0 ? 'Mech owes' : 'You owe'}</span>
                      <span className="text-base">{fmtCurrency(Math.abs(m.balance_owing || 0))}</span>
                    </div>
                    <div className="flex-1" />
                    {/* Anchor / reset opening balance — fixes the case
                        where legacy data made the opening balance wrong
                        (e.g. $4500 owing when SM thinks it should be 0). */}
                    <button
                      type="button"
                      onClick={() => setOpeningBalanceModal({ open: true, mech: m })}
                      className="px-3 py-1.5 text-xs rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 font-medium flex items-center gap-1.5"
                      data-testid={`ci-mech-anchor-${m.mechanic_id}`}
                      title="Set or clear the opening balance — useful if legacy data is showing a wrong starting figure."
                    >
                      ⚓ Set Opening Balance
                    </button>
                    <button
                      type="button"
                      onClick={() => generateEmployeeReport(m.mechanic_id, m.mechanic_name)}
                      className="px-3 py-1.5 text-xs rounded bg-slate-900 text-white hover:bg-slate-700 font-medium flex items-center gap-1.5"
                      data-testid={`ci-mech-print-${m.mechanic_id}`}
                      title="Generate a print-ready report for this employee for the selected period."
                    >
                      📄 Generate Report
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Auto-saves on edit. Set all four values to 0 to clear an entry. Weekends are shaded. <strong>Balance Owing</strong> = (running) Opening + Wage − Parts − Wage Paid. Positive ⇒ you owe the mechanic. Negative ⇒ mechanic owes you (parts &gt; earnings).
                  </p>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Footer note */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <p className="text-xs text-slate-600">
          <strong>Cash remaining:</strong> Cash in Hand ({fmtCurrency(data.cash_accumulated)}) − Wages Paid in Cash ({fmtCurrency(data.wages_cash_paid || 0)}) − Incentives ({fmtCurrency(data.total_incentives_paid)}) = <strong>{fmtCurrency(cashRemaining)}</strong>.
          <span className="ml-2 text-emerald-700">GST saved via parts-offset: <strong>{fmtCurrency(data.gst_saved || 0)}</strong>.</span>
          {data.total_auto_wages > 0 && (
            <span className="ml-2 text-indigo-700">
              <Lock className="h-3 w-3 inline mr-0.5" />
              Auto-wages: <strong>{fmtCurrency(data.total_auto_wages)}</strong> of {fmtCurrency(data.total_wages || 0)} total.
            </span>
          )}
        </p>
      </Card>

      <PayrollSetupModal
        BACKEND_URL={BACKEND_URL}
        open={showPayrollModal}
        onOpenChange={setShowPayrollModal}
        onSaved={fetchData}
      />

      <OpeningBalanceModal
        BACKEND_URL={BACKEND_URL}
        state={openingBalanceModal}
        onClose={() => setOpeningBalanceModal({ open: false, mech: null })}
        onSaved={fetchData}
      />
    </div>
  );
}

// --- Opening Balance Anchor Modal ---
// Lets SM anchor a mechanic's opening balance owing at a known good
// figure, e.g. "as of 1-Jun-2026, Bob owes me $0". Used when legacy
// cash-incentive entries (pre-wage_paid era) inflate the auto-computed
// opening balance with wages that were actually paid in cash off-books.
function OpeningBalanceModal({ BACKEND_URL, state, onClose, onSaved }) {
  const mech = state.mech;
  const [amount, setAmount] = useState('');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state.open && mech) {
      const existing = mech.opening_balance_override;
      setAmount(existing != null ? String(existing) : '0');
      setAsOf(mech.opening_balance_as_of || new Date().toISOString().slice(0, 10));
    }
  }, [state.open, mech]);

  if (!state.open || !mech) return null;

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const body = clear
        ? { mechanic_id: mech.mechanic_id, clear: true }
        : { mechanic_id: mech.mechanic_id, as_of_date: asOf, opening_balance: parseFloat(amount) || 0 };
      const res = await fetch(`${BACKEND_URL}/api/super-admin/cash-incentives/opening-balance`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      toast.success(clear ? 'Opening balance cleared — back to auto-computed.' : `Opening balance anchored at $${amount} as of ${asOf}`);
      onClose();
      onSaved();
    } catch (_e) {
      toast.error('Network error — please retry');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="opening-balance-modal">
        <DialogHeader>
          <DialogTitle>⚓ Set Opening Balance — {mech.mechanic_name}</DialogTitle>
          <DialogDescription>
            Anchor this mechanic's running balance at a known good figure.
            Useful when legacy entries inflate the auto-computed opening
            (e.g. system shows $4,500 owing but you've already paid off
            past wages in cash). After anchoring, the system computes
            forward from this date using the Wage / Parts / Wage Paid
            you've entered.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">As of date</Label>
            <Input
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value || new Date().toISOString().slice(0, 10))}
              data-testid="opening-balance-asof"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Entries on or before this date are ignored. Entries after are added on top.
            </p>
          </div>
          <div>
            <Label className="text-xs">Opening balance at that date ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0 = settled · positive = you owe mechanic · negative = mech owes you"
              data-testid="opening-balance-amount"
            />
          </div>
          {mech.opening_balance_override != null && (
            <div className="text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 p-2 rounded">
              Currently anchored at <strong>${mech.opening_balance_override.toFixed(2)}</strong> as of {mech.opening_balance_as_of}.
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {mech.opening_balance_override != null && (
            <Button
              variant="outline"
              onClick={() => save(true)}
              disabled={saving}
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="opening-balance-clear"
            >
              Clear anchor (back to auto)
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={() => save(false)}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="opening-balance-save"
          >
            {saving ? 'Saving…' : 'Anchor opening balance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
